import type {
	GraphNode,
	NodePosition,
	PositionsMap,
	EdgeElements,
	NodeElements
} from './types';
import { badgeUrls, badgeFilters, typeColors } from './config';
import { getNodeSize, getNodeRadius, getBadgeStyle, getNodeTier } from './svg';
import { generateTooltipHTML, formatDescription } from './tooltip';

// Highlight state management
export function clearHighlights(nodeElements: NodeElements, edgeElements: EdgeElements): void {
	// Only clear elements that were actually highlighted/dimmed
	for (const nodeId of [...lastHighlightedNodes, ...lastDimmedNodes]) {
		nodeElements[nodeId]?.classList.remove('node-dim', 'node-preview-dim', 'node-related', 'node-hovered');
	}
	for (const edgeKey of [...lastHighlightedEdges, ...lastDimmedEdges]) {
		edgeElements[edgeKey]?.classList.remove('connection-dim', 'connection-preview-dim', 'connection-related');
	}
	for (const nodeId of lastSuppressedSelectedNodes) {
		nodeElements[nodeId]?.classList.remove('selection-suppressed');
	}
	for (const edgeKey of lastSuppressedSelectedEdges) {
		edgeElements[edgeKey]?.classList.remove('selection-suppressed');
	}
	clearHighlightState();
	highlightSelectedEdges(selectedNodes, edgeElements);
}

// Highlight nodes by type (zone)
export function highlightNodesByType(
	nodeType: string,
	nodes: Record<string, GraphNode>,
	nodeElements: NodeElements,
	edgeElements: EdgeElements
): void {
	// Normalize type: undefined/empty becomes 'general'
	const targetType = nodeType || 'general';
	
	const newHighlightedNodes = new Set<string>();
	const newDimmedNodes = new Set<string>();
	const suppressedSelectedNodes = new Set<string>();
	
	Object.entries(nodeElements).forEach(([nodeId, element]) => {
		const node = nodes[nodeId];
		if (!node) return;
		
		const nodeTypeValue = node.type || 'general';
		
		element.classList.remove('node-dim', 'node-preview-dim', 'node-related', 'node-hovered');
		element.classList.remove('selection-suppressed');
		
		if (nodeTypeValue === targetType) {
			element.classList.add('node-hovered');
			newHighlightedNodes.add(nodeId);
		} else {
			element.classList.add('node-dim');
			newDimmedNodes.add(nodeId);
		}

		if (selectedNodes.has(nodeId)) {
			element.classList.add('selection-suppressed');
			suppressedSelectedNodes.add(nodeId);
		}
	});

	// Dim all edges when highlighting by type
	const newDimmedEdges = new Set<string>();
	const suppressedSelectedEdges = new Set<string>();
	Object.values(edgeElements).forEach((element) => {
		element.classList.remove('connection-dim', 'connection-preview-dim', 'connection-related');
		element.classList.remove('selection-suppressed');
		element.classList.add('connection-dim');
		if (element.classList.contains('connection-selected')) {
			element.classList.add('selection-suppressed');
		}
	});
	Object.keys(edgeElements).forEach(key => newDimmedEdges.add(key));
	Object.entries(edgeElements).forEach(([key, element]) => {
		if (element.classList.contains('selection-suppressed')) suppressedSelectedEdges.add(key);
	});

	// Update tracking state
	lastHighlightedNodes = newHighlightedNodes;
	lastDimmedNodes = newDimmedNodes;
	lastHighlightedEdges = new Set();
	lastDimmedEdges = newDimmedEdges;
	lastSuppressedSelectedNodes = suppressedSelectedNodes;
	lastSuppressedSelectedEdges = suppressedSelectedEdges;
}

// Highlight specific nodes by their IDs (for effects summary panel)
export function highlightNodesByIds(
	nodeIds: string[],
	nodes: Record<string, GraphNode>,
	nodeElements: NodeElements,
	edgeElements: EdgeElements
): void {
	const targetIds = new Set(nodeIds);

	const newHighlightedNodes = new Set<string>();
	const newDimmedNodes = new Set<string>();

	Object.entries(nodeElements).forEach(([nodeId, element]) => {
		element.classList.remove('node-dim', 'node-preview-dim', 'node-related', 'node-hovered');
		if (targetIds.has(nodeId)) {
			element.classList.add('node-hovered');
			newHighlightedNodes.add(nodeId);
		} else {
			element.classList.add('node-dim');
			newDimmedNodes.add(nodeId);
		}
	});

	// Dim all edges
	const newDimmedEdges = new Set<string>();
	Object.values(edgeElements).forEach((element) => {
		element.classList.remove('connection-dim', 'connection-preview-dim', 'connection-related');
		element.classList.add('connection-dim');
	});
	Object.keys(edgeElements).forEach(key => newDimmedEdges.add(key));

	// Update tracking state
	lastHighlightedNodes = newHighlightedNodes;
	lastDimmedNodes = newDimmedNodes;
	lastHighlightedEdges = new Set();
	lastDimmedEdges = newDimmedEdges;
}

// Ancestor path collection for packed layout
export function collectAncestorPath(
	node: GraphNode,
	nodes: Record<string, GraphNode>
): { ancestorNodes: string[]; ancestorEdges: string[] } {
	const ancestorNodes: string[] = [];
	const ancestorEdges: string[] = [];
	let current = node;

	while (current.highlightParentId && nodes[current.highlightParentId]) {
		const parent = nodes[current.highlightParentId];
		ancestorNodes.push(parent.id);
		ancestorEdges.push(`${parent.id}|${current.id}`);
		current = parent;
	}

	return { ancestorNodes, ancestorEdges };
}

// Ancestor path collection for tree-first layout
export function collectTreeAncestorPath(
	node: GraphNode,
	nodes: Record<string, GraphNode>
): { ancestorNodes: string[]; ancestorEdges: string[] } {
	const ancestorNodes: string[] = [];
	const ancestorEdges: string[] = [];
	let current = node;

	while (current.primaryParent && nodes[current.primaryParent]) {
		const parent = nodes[current.primaryParent];
		ancestorNodes.push(parent.id);
		ancestorEdges.push(`${parent.id}|${current.id}`);
		current = parent;
	}

	return { ancestorNodes, ancestorEdges };
}

// Highlight related nodes and edges for packed layout
// Helper: try to find the edge key for a connection between two nodes
// Edges are stored as "parent|child" (not sorted), so check both directions
function findEdgeKey(a: string, b: string, edgeElements: EdgeElements): string | null {
	if (edgeElements[`${a}|${b}`]) return `${a}|${b}`;
	if (edgeElements[`${b}|${a}`]) return `${b}|${a}`;
	return null;
}

// Highlight the path that would be selected if this node were clicked
export function highlightSelectionPreview(
	node: GraphNode,
	nodes: Record<string, GraphNode>,
	nodeElements: NodeElements,
	edgeElements: EdgeElements
): void {
	const hoveredId = node.id;
	const relatedNodes = new Set<string>([hoveredId]);
	const relatedEdges = new Set<string>();

	if (selectedNodes.has(hoveredId)) {
		// Already selected — just highlight this node and edges to selected neighbors
		for (const parentId of node.parents) {
			if (selectedNodes.has(parentId)) {
				relatedNodes.add(parentId);
				const ek = findEdgeKey(parentId, hoveredId, edgeElements);
				if (ek) relatedEdges.add(ek);
			}
		}
		for (const childId of node.children) {
			if (selectedNodes.has(childId)) {
				relatedNodes.add(childId);
				const ek = findEdgeKey(hoveredId, childId, edgeElements);
				if (ek) relatedEdges.add(ek);
			}
		}
	} else {
		// Compute what would be selected: shortest path to any selected node
		const pathToSelect = findShortestPathToSelected(hoveredId, nodes, selectedNodes);
		for (const pathNodeId of pathToSelect) {
			relatedNodes.add(pathNodeId);
		}

		// Find edges between consecutive path nodes
		for (let i = 0; i < pathToSelect.length - 1; i++) {
			const ek = findEdgeKey(pathToSelect[i], pathToSelect[i + 1], edgeElements);
			if (ek) relatedEdges.add(ek);
		}

		// Find edge from last path node to the selected anchor it connects to
		if (pathToSelect.length > 0) {
			const lastPathNode = pathToSelect[pathToSelect.length - 1];
			const lpn = nodes[lastPathNode];
			if (lpn) {
				const neighbors = new Set<string>();
				for (const p of lpn.parents) neighbors.add(p);
				for (const c of lpn.children) neighbors.add(c);
				if (lpn.linked) for (const l of lpn.linked) neighbors.add(l);
				for (const nId of neighbors) {
					if (selectedNodes.has(nId)) {
						relatedNodes.add(nId);
						const ek = findEdgeKey(lastPathNode, nId, edgeElements);
						if (ek) relatedEdges.add(ek);
					}
				}
			}
		}
	}

	applyHighlightIncremental(relatedNodes, relatedEdges, nodeElements, edgeElements, hoveredId);
}

// Selection preview is layout-independent — same BFS logic for all layouts
// Both highlightRelatedPacked and highlightRelatedTreeFirst are replaced by highlightSelectionPreview above.

// Render a single node group
export function renderNode(
	position: NodePosition,
	iconDefault: string | undefined,
	iconActiveOrMainGroup: string | SVGGElement | undefined,
	mainGroupArg?: SVGGElement
): SVGGElement {
	const iconActive = typeof iconActiveOrMainGroup === 'string' ? iconActiveOrMainGroup : undefined;
	const mainGroup = iconActiveOrMainGroup instanceof SVGGElement ? iconActiveOrMainGroup : mainGroupArg;
	if (!mainGroup) {
		throw new Error('renderNode requires an SVG group container');
	}

	const node = position.node;
	const type = node.type || 'general';
	const badgeTint = node.id === 'node1' ? 'root' : badgeFilters[type] ? type : 'general';

	const isCap = node.size === 'node_capstone';
	const isMajor = node.size === 'node_major';
	const isRoot = node.id === 'node1';
	const isMinor = !isCap && !isMajor && !isRoot;

	const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
	g.setAttribute('class', 'node');
	g.setAttribute('transform', `translate(${position.x}, ${position.y})`);
	g.setAttribute('data-node-id', node.id);

	// Determine size and badge
	let size: number;
	let badgeUrl: string;
	if (isRoot) {
		size = 48;
		badgeUrl = badgeUrls.root;
	} else if (isCap) {
		size = 72;
		badgeUrl = badgeUrls.capstone;
	} else if (isMajor) {
		size = 44;
		badgeUrl = badgeUrls.major;
	} else {
		size = 32;
		badgeUrl = badgeUrls.minor;
	}

	const iconScale = isMinor ? 0.6 : isMajor ? 0.65 : isRoot ? 0.68 : 0.72;
	const halfSize = size / 2;

	// Hover/category highlight background (visible behind badge)
	const highlightBg = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
	highlightBg.setAttribute('r', String(halfSize + 4));
	highlightBg.setAttribute('class', 'node-highlight-bg');
	highlightBg.setAttribute('fill', 'var(--color-node-highlight)');
	highlightBg.setAttribute('opacity', '0');
	g.appendChild(highlightBg);

	// Selection ring (visible when node is selected)
	const selectionRing = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
	selectionRing.setAttribute('r', String(halfSize + 4));
	selectionRing.setAttribute('class', 'node-selection-ring');
	selectionRing.setAttribute('fill', 'none');
	selectionRing.setAttribute('stroke', 'var(--color-accent-primary)');
	selectionRing.setAttribute('stroke-width', '3');
	selectionRing.setAttribute('opacity', '0');
	selectionRing.setAttribute('filter', 'url(#selectionGlow)');
	g.appendChild(selectionRing);

	// P0-4: Cache reference to selection ring (30-86ms savings per access)
	(g as any).__selectionRing = selectionRing;

	// Badge image
	const badge = document.createElementNS('http://www.w3.org/2000/svg', 'image');
	badge.setAttribute('href', badgeUrl);
	badge.setAttribute('width', String(size));
	badge.setAttribute('height', String(size));
	badge.setAttribute('x', String(-halfSize));
	badge.setAttribute('y', String(-halfSize));
	badge.setAttribute('class', 'badge-img');
	badge.setAttribute('style', getBadgeStyle(node, badgeTint));
	g.appendChild(badge);

	// Icon on top - use default icon initially, store active icon for later
	const iconToUse = iconDefault || iconActive;
	if (iconToUse) {
		const iconSize = size * iconScale;
		const icon = document.createElementNS('http://www.w3.org/2000/svg', 'image');
		icon.setAttribute('href', iconToUse);
		icon.setAttribute('width', String(iconSize));
		icon.setAttribute('height', String(iconSize));
		icon.setAttribute('x', String(-iconSize / 2));
		icon.setAttribute('y', String(-iconSize / 2));
		icon.setAttribute('class', 'node-icon');
		icon.setAttribute('data-default-src', iconDefault || '');
		icon.setAttribute('data-active-src', iconActive || '');
		g.appendChild(icon);
		
		// P0-4: Cache reference to icon (30-86ms savings per querySelector)
		(g as any).__nodeIcon = icon;
	}

	mainGroup.appendChild(g);
	return g;
}

export function setTimelineDimming(nodeElements: NodeElements, visibleIds: Set<string>, edgeElements?: EdgeElements): void {
	for (const [nodeId, element] of Object.entries(nodeElements)) {
		if (visibleIds.has(nodeId)) {
			element.classList.remove('node-timeline-dim');
		} else {
			element.classList.add('node-timeline-dim');
		}
	}

	if (edgeElements) {
		for (const [edgeKey, element] of Object.entries(edgeElements)) {
			const [from, to] = edgeKey.split('|');
			const bothVisible = visibleIds.has(from) && visibleIds.has(to);
			if (bothVisible) {
				element.style.display = '';
			} else {
				element.style.display = 'none';
			}
		}
	}
}

export function clearTimelineDimming(nodeElements: NodeElements, edgeElements?: EdgeElements): void {
	for (const element of Object.values(nodeElements)) {
		element.classList.remove('node-timeline-dim');
	}
	if (edgeElements) {
		for (const element of Object.values(edgeElements)) {
			element.style.display = '';
		}
	}
}

// Track selected nodes and their unlock order
const selectedNodes = new Set<string>();
let unlockOrder: string[] = [];
let onSelectionChange: ((selectedIds: string[]) => void) | null = null;

export function setSelectionCallback(callback: (selectedIds: string[]) => void): void {
	onSelectionChange = callback;
}

export function getSelectedNodes(): string[] {
	// Return in unlock order to maintain chain validity
	return [...unlockOrder];
}

export function clearAllSelections(nodeElements: NodeElements, edgeElements?: EdgeElements): void {
	// Clear in reverse order (LIFO) to maintain chain invariants
	for (let i = unlockOrder.length - 1; i >= 0; i--) {
		const nodeId = unlockOrder[i];
		const element = nodeElements[nodeId];
		if (element) {
			element.classList.remove('node-selected');
			// P0-4: Use cached icon reference instead of querySelector (30-86ms savings)
			const icon = (element as any).__nodeIcon as SVGImageElement | null;
			if (icon) {
				const defaultSrc = icon.getAttribute('data-default-src');
				if (defaultSrc) {
					icon.setAttribute('href', defaultSrc);
				}
			}
		}
		selectedNodes.delete(nodeId);
	}
	unlockOrder.length = 0;
	
	// Clear edge highlighting
	if (edgeElements) {
		highlightSelectedEdges(selectedNodes, edgeElements);
	}
	
	onSelectionChange?.([]);
}

function setNodeSelectedState(element: SVGGElement, selected: boolean): void {
	if (selected) {
		element.classList.add('node-selected');
	} else {
		element.classList.remove('node-selected');
	}

	const icon = (element as any).__nodeIcon as SVGImageElement | null;
	if (!icon) return;

	const attr = selected ? 'data-active-src' : 'data-default-src';
	const src = icon.getAttribute(attr);
	if (src) {
		icon.setAttribute('href', src);
	}
}

function applySelectionState(
	nextSelectedNodes: Set<string>,
	nextUnlockOrder: string[],
	nodeElements: NodeElements
): void {
	for (const selectedId of selectedNodes) {
		if (!nextSelectedNodes.has(selectedId)) {
			const element = nodeElements[selectedId];
			if (element) setNodeSelectedState(element, false);
		}
	}

	for (const selectedId of nextSelectedNodes) {
		const element = nodeElements[selectedId];
		if (element) setNodeSelectedState(element, true);
	}

	selectedNodes.clear();
	for (const selectedId of nextSelectedNodes) {
		selectedNodes.add(selectedId);
	}

	unlockOrder = [...nextUnlockOrder];
}

function pruneSelectionWithoutNode(
	removedNodeId: string,
	nodes: Record<string, GraphNode>
): { selected: Set<string>; order: string[] } | null {
	const remainingOrder = unlockOrder.filter((id) => id !== removedNodeId);
	if (!remainingOrder.includes('node1')) {
		return null;
	}

	const remainingSelected = new Set(remainingOrder);
	const reachable = new Set<string>(['node1']);
	const queue = ['node1'];

	while (queue.length > 0) {
		const currentId = queue.shift()!;
		const currentNode = nodes[currentId];
		if (!currentNode) continue;

		for (const neighborId of currentNode.neighbors) {
			if (!remainingSelected.has(neighborId) || reachable.has(neighborId)) continue;
			reachable.add(neighborId);
			queue.push(neighborId);
		}
	}

	return {
		selected: reachable,
		order: remainingOrder.filter((id) => reachable.has(id))
	};
}

export function toggleNodeSelection(
	nodeId: string,
	nodeElements: NodeElements,
	nodes?: Record<string, GraphNode>,
	edgeElements?: EdgeElements
): boolean {
	const element = nodeElements[nodeId];
	if (!element) return false;

	if (selectedNodes.has(nodeId)) {
		if (nodeId === 'node1') {
			applySelectionState(new Set(['node1']), ['node1'], nodeElements);
			if (edgeElements) {
				highlightSelectedEdges(selectedNodes, edgeElements);
			}
			onSelectionChange?.([...unlockOrder]);
			return true;
		}

		// Remove the node and keep only the remaining selection that is still connected to root.
		// This avoids blocking on stale auto-added path nodes, without selecting any replacement nodes.
		if (nodes && selectedNodes.size > 1) {
			const pruned = pruneSelectionWithoutNode(nodeId, nodes);
			if (!pruned) {
				return true;
			}

			applySelectionState(pruned.selected, pruned.order, nodeElements);
		} else {
			selectedNodes.delete(nodeId);
			unlockOrder = unlockOrder.filter(id => id !== nodeId);
			setNodeSelectedState(element, false);
		}
	} else {
		// Selecting a new node

		// If nothing is selected yet, auto-select root first
		if (selectedNodes.size === 0 && nodeId !== 'node1' && nodes) {
			const rootEl = nodeElements['node1'];
			if (rootEl) {
				selectedNodes.add('node1');
				unlockOrder.push('node1');
				rootEl.classList.add('node-selected');
				const rootIcon = (rootEl as any).__nodeIcon as SVGImageElement | null;
				if (rootIcon) {
					const activeSrc = rootIcon.getAttribute('data-active-src');
					if (activeSrc) rootIcon.setAttribute('href', activeSrc);
				}
			}
		}

		// Check if any adjacent nodes (parents or children) are already selected
		const hasAdjacentSelected = nodes ? checkHasAdjacentSelected(nodeId, nodes, selectedNodes) : false;
		
		if (!hasAdjacentSelected && nodes && nodeId !== 'node1') {
			// No adjacent selected nodes - find shortest path to any selected node
			const pathToSelected = findShortestPathToSelected(nodeId, nodes, selectedNodes);
			// Reverse so we add nodes from the selected chain outward to the target
			const orderedPath = pathToSelected.reverse();
			// Select all nodes on the path that aren't already selected
			for (const pathNodeId of orderedPath) {
				if (!selectedNodes.has(pathNodeId)) {
					const pathElement = nodeElements[pathNodeId];
					if (pathElement) {
						selectedNodes.add(pathNodeId);
						unlockOrder.push(pathNodeId);
						pathElement.classList.add('node-selected');
						// P0-4: Use cached icon reference (30-86ms savings vs querySelector)
						const icon = (pathElement as any).__nodeIcon as SVGImageElement | null;
						if (icon) {
							const activeSrc = icon.getAttribute('data-active-src');
							if (activeSrc) {
								icon.setAttribute('href', activeSrc);
							}
						}
					}
				}
			}
		} else {
			// Has adjacent selected or is start node - just select this node
			selectedNodes.add(nodeId);
			unlockOrder.push(nodeId);
			element.classList.add('node-selected');
			// P0-4: Use cached icon reference (30-86ms savings vs querySelector)
			const icon = (element as any).__nodeIcon as SVGImageElement | null;
			if (icon) {
				const activeSrc = icon.getAttribute('data-active-src');
				if (activeSrc) {
					icon.setAttribute('href', activeSrc);
				}
			}
		}
	}

	// Update edge highlighting based on selected nodes
	if (edgeElements) {
		highlightSelectedEdges(selectedNodes, edgeElements);
	}

	// Refresh hover highlight since selection state changed under the cursor
	if (currentTooltipNode && nodes && nodeElements && edgeElements) {
		const hoveredNode = nodes[currentTooltipNode];
		if (hoveredNode) {
			highlightSelectionPreview(hoveredNode, nodes, nodeElements, edgeElements);
		}
	}

	onSelectionChange?.(getSelectedNodes());
	return selectedNodes.has(nodeId);
}

// Highlight edges between selected nodes
export function highlightSelectedEdges(
	selectedNodes: Set<string>,
	edgeElements: EdgeElements
): void {
	Object.entries(edgeElements).forEach(([edgeKey, element]) => {
		// Parse edge key format: "parentId|childId"
		const [parentId, childId] = edgeKey.split('|');
		if (parentId && childId) {
			const isSelected = selectedNodes.has(parentId) && selectedNodes.has(childId);
			if (isSelected) {
				element.classList.add('connection-selected');
				// Ensure it's visible
				element.setAttribute('opacity', '0.8');
			} else {
				element.classList.remove('connection-selected');
				// Reset to default opacity if not selected
				const isTree = element.classList.contains('tree-connection');
				const isSecondary = element.classList.contains('secondary-connection');
				element.setAttribute('opacity', isTree ? '0.55' : isSecondary ? '0.55' : '0.55');
			}
		}
	});
}

// Check if any parent or child of the node is already selected
function checkHasAdjacentSelected(
	nodeId: string,
	nodes: Record<string, GraphNode>,
	selectedNodes: Set<string>
): boolean {
	const node = nodes[nodeId];
	if (!node) return false;

	const neighbors = new Set<string>();
	for (const p of node.parents) neighbors.add(p);
	for (const c of node.children) neighbors.add(c);
	if (node.linked) for (const l of node.linked) neighbors.add(l);

	for (const nId of neighbors) {
		if (selectedNodes.has(nId)) return true;
	}

	return false;
}

// Find shortest path from target node to the nearest already-selected node
// using BFS across the graph's linked/parent/child connections
function findShortestPathToSelected(
	targetNodeId: string,
	nodes: Record<string, GraphNode>,
	selectedNodes: Set<string>
): string[] {
	// If target is already selected, nothing to do
	if (selectedNodes.has(targetNodeId)) return [];
	
	// If node1 is selected, we can always fall back to pathing to it
	// But first try to find a shorter path to any closer selected node
	
	const visited = new Set<string>();
	const queue: { nodeId: string; path: string[] }[] = [{ nodeId: targetNodeId, path: [targetNodeId] }];
	visited.add(targetNodeId);
	
	while (queue.length > 0) {
		const { nodeId, path } = queue.shift()!;
		const node = nodes[nodeId];
		if (!node) continue;
		
		// Get all neighbors (parents + children + linked)
		const neighbors = new Set<string>();
		for (const p of node.parents) neighbors.add(p);
		for (const c of node.children) neighbors.add(c);
		if (node.linked) {
			for (const l of node.linked) neighbors.add(l);
		}
		
		for (const neighborId of neighbors) {
			if (visited.has(neighborId)) continue;
			if (!nodes[neighborId]) continue;
			
			const newPath = [...path, neighborId];
			
			// Found a selected node — return the path TO it (excluding the selected node itself)
			if (selectedNodes.has(neighborId)) {
				return newPath.slice(0, -1);
			}
			
			visited.add(neighborId);
			queue.push({ nodeId: neighborId, path: newPath });
		}
	}
	
	// Fallback: no selected node reachable (shouldn't happen if node1 is selected)
	return [targetNodeId];
}

export function isNodeSelected(nodeId: string): boolean {
	return selectedNodes.has(nodeId);
}

// Track tooltip state globally to prevent flicker
let tooltipHideTimeout: ReturnType<typeof setTimeout> | null = null;
let currentTooltipNode: string | null = null;

// Track last highlight state to do incremental updates instead of iterating all elements
let lastHighlightedNodes = new Set<string>();
let lastDimmedNodes = new Set<string>();
let lastHighlightedEdges = new Set<string>();
let lastDimmedEdges = new Set<string>();
let lastSuppressedSelectedNodes = new Set<string>();
let lastSuppressedSelectedEdges = new Set<string>();

function applyHighlightIncremental(
	relatedNodes: Set<string>,
	relatedEdges: Set<string>,
	nodeElements: NodeElements,
	edgeElements: EdgeElements,
	hoveredId: string
): void {
	const allNodes = new Set([...lastHighlightedNodes, ...lastDimmedNodes, ...relatedNodes]);
	const allEdges = new Set([...lastHighlightedEdges, ...lastDimmedEdges, ...relatedEdges]);

	const newDimmedNodes = new Set<string>();
	const newDimmedEdges = new Set<string>();

	for (const nodeId of allNodes) {
		const el = nodeElements[nodeId];
		if (!el) continue;
		if (nodeId === hoveredId) {
			el.classList.remove('node-dim', 'node-preview-dim', 'node-related');
			if (!el.classList.contains('node-hovered')) el.classList.add('node-hovered');
		} else if (relatedNodes.has(nodeId)) {
			el.classList.remove('node-dim', 'node-preview-dim', 'node-hovered');
			if (!el.classList.contains('node-related')) el.classList.add('node-related');
		} else {
			el.classList.remove('node-preview-dim', 'node-related', 'node-hovered');
			if (!el.classList.contains('node-dim')) el.classList.add('node-dim');
			newDimmedNodes.add(nodeId);
		}
	}

	for (const edgeKey of allEdges) {
		const el = edgeElements[edgeKey];
		if (!el) continue;
		if (relatedEdges.has(edgeKey)) {
			el.classList.remove('connection-dim', 'connection-preview-dim');
			if (!el.classList.contains('connection-related')) el.classList.add('connection-related');
		} else {
			el.classList.remove('connection-preview-dim', 'connection-related');
			if (!el.classList.contains('connection-dim')) el.classList.add('connection-dim');
			newDimmedEdges.add(edgeKey);
		}
	}

	lastHighlightedNodes = relatedNodes;
	lastDimmedNodes = newDimmedNodes;
	lastHighlightedEdges = relatedEdges;
	lastDimmedEdges = newDimmedEdges;
}

export function clearHighlightState(): void {
	lastHighlightedNodes = new Set();
	lastDimmedNodes = new Set();
	lastHighlightedEdges = new Set();
	lastDimmedEdges = new Set();
	lastSuppressedSelectedNodes = new Set();
	lastSuppressedSelectedEdges = new Set();
}

// Highlight the selection path with softer dimming (for unlockable list hover)
export function highlightSelectionPath(
	node: GraphNode,
	nodes: Record<string, GraphNode>,
	nodeElements: NodeElements,
	edgeElements: EdgeElements,
	suppressSelection: boolean = false
): void {
	const hoveredId = node.id;
	const pathNodes = new Set<string>([hoveredId]);
	const pathEdges = new Set<string>();

	if (!selectedNodes.has(hoveredId)) {
		// Compute shortest path to any selected node
		const pathToSelect = findShortestPathToSelected(hoveredId, nodes, selectedNodes);
		for (const pathNodeId of pathToSelect) {
			pathNodes.add(pathNodeId);
		}

		// Find edges between consecutive path nodes
		for (let i = 0; i < pathToSelect.length - 1; i++) {
			const ek = findEdgeKey(pathToSelect[i], pathToSelect[i + 1], edgeElements);
			if (ek) pathEdges.add(ek);
		}

		// Find edge from last path node to the selected anchor
		if (pathToSelect.length > 0) {
			const lastPathNode = pathToSelect[pathToSelect.length - 1];
			const lpn = nodes[lastPathNode];
			if (lpn) {
				const neighbors = new Set<string>();
				for (const p of lpn.parents) neighbors.add(p);
				for (const c of lpn.children) neighbors.add(c);
				if (lpn.linked) for (const l of lpn.linked) neighbors.add(l);
				for (const nId of neighbors) {
					if (selectedNodes.has(nId)) {
						if (!suppressSelection) {
							pathNodes.add(nId);
							const ek = findEdgeKey(lastPathNode, nId, edgeElements);
							if (ek) pathEdges.add(ek);
						}
					}
				}
			}
		}
	}

	const newDimmedNodes = new Set<string>();
	const newDimmedEdges = new Set<string>();
	const suppressedSelectedNodes = new Set<string>();
	const suppressedSelectedEdges = new Set<string>();

	for (const [nodeId, el] of Object.entries(nodeElements)) {
		el.classList.remove('node-dim', 'node-preview-dim', 'node-related', 'node-hovered');
		el.classList.remove('selection-suppressed');
		if (nodeId === hoveredId) {
			el.classList.add('node-hovered');
		} else if (pathNodes.has(nodeId)) {
			el.classList.add('node-related');
		} else {
			el.classList.add('node-preview-dim');
			newDimmedNodes.add(nodeId);
		}

		if (suppressSelection && selectedNodes.has(nodeId)) {
			el.classList.add('selection-suppressed');
			suppressedSelectedNodes.add(nodeId);
		}
	}

	for (const [edgeKey, el] of Object.entries(edgeElements)) {
		const [fromId, toId] = edgeKey.split('|');
		const touchesSelectedNode = Boolean(fromId && toId && (selectedNodes.has(fromId) || selectedNodes.has(toId)));
		el.classList.remove('connection-dim', 'connection-preview-dim', 'connection-related');
		el.classList.remove('selection-suppressed');
		if (pathEdges.has(edgeKey) && !(suppressSelection && touchesSelectedNode)) {
			el.classList.add('connection-related');
		} else {
			el.classList.add('connection-preview-dim');
			newDimmedEdges.add(edgeKey);
		}

		if (suppressSelection && touchesSelectedNode) {
			el.classList.remove('connection-selected', 'connection-related');
			el.classList.add('selection-suppressed');
			suppressedSelectedEdges.add(edgeKey);
		}
	}

	lastHighlightedNodes = pathNodes;
	lastHighlightedEdges = pathEdges;
	lastDimmedNodes = newDimmedNodes;
	lastDimmedEdges = newDimmedEdges;
	lastSuppressedSelectedNodes = suppressedSelectedNodes;
	lastSuppressedSelectedEdges = suppressedSelectedEdges;
}

// P0: Cache tooltip dimensions to avoid forced layout (getBoundingClientRect = 30-50ms)
let cachedTooltipRect: DOMRect | null = null;
let tooltipRafId: number | null = null;

// Setup tooltip and click handlers for a node
export function setupNodeHandlers(
	g: SVGGElement,
	node: GraphNode,
	iconHref: string | undefined,
	tooltip: HTMLElement,
	nodes: Record<string, GraphNode>,
	nodeElements: NodeElements,
	edgeElements: EdgeElements,
	isTreeFirst: boolean,
	onNodeClick?: (node: GraphNode) => void,
	onNodeHover?: (nodeId: string | null) => void
): void {
	g.addEventListener('mouseenter', (e) => {
		// Cancel any pending hide
		if (tooltipHideTimeout) {
			clearTimeout(tooltipHideTimeout);
			tooltipHideTimeout = null;
		}
		currentTooltipNode = node.id;
		
		highlightSelectionPreview(node, nodes, nodeElements, edgeElements);
		showTooltip(e as MouseEvent, node, iconHref, tooltip);
		onNodeHover?.(node.id);
	});

	g.addEventListener('mouseleave', () => {
		// Delay hiding to allow moving to adjacent nodes without flicker
		tooltipHideTimeout = setTimeout(() => {
			if (currentTooltipNode === node.id) {
				clearHighlights(nodeElements, edgeElements);
				hideTooltip(tooltip);
				currentTooltipNode = null;
				onNodeHover?.(null);
			}
		}, 50); // 50ms delay prevents flicker when moving between nodes
	});

	g.addEventListener('mousemove', (e) => {
		moveTooltip(e as MouseEvent, tooltip);
	});

	g.addEventListener('click', (e) => {
		e.stopPropagation();
		onNodeClick?.(node);
	});
}

// Tooltip display functions
export function showTooltip(
	event: MouseEvent,
	node: GraphNode,
	iconHref: string | undefined,
	tooltip: HTMLElement
): void {
	tooltip.innerHTML = generateTooltipHTML(node, iconHref);
	tooltip.style.display = 'block';
	// P0: Cache dimensions after showing to avoid forced layout
	cachedTooltipRect = tooltip.getBoundingClientRect();
	moveTooltip(event, tooltip);
}

export function hideTooltip(tooltip: HTMLElement): void {
	tooltip.style.display = 'none';
	cachedTooltipRect = null;
	// Cancel any pending RAF
	if (tooltipRafId) {
		cancelAnimationFrame(tooltipRafId);
		tooltipRafId = null;
	}
}

export function moveTooltip(event: MouseEvent, tooltip: HTMLElement): void {
	// P0: Batch tooltip position updates with RAF to avoid forced layout thrashing
	if (tooltipRafId) {
		cancelAnimationFrame(tooltipRafId);
	}
	
	tooltipRafId = requestAnimationFrame(() => {
		const x = event.clientX + 15;
		const y = event.clientY - 15;
		
		// P0: Use cached rect or skip bounds check if not available
		// This avoids the 30-50ms getBoundingClientRect call on every mousemove
		let finalX = x;
		let finalY = y;
		
		if (cachedTooltipRect) {
			finalX = Math.min(x, window.innerWidth - cachedTooltipRect.width - 20);
			finalY = Math.max(y - cachedTooltipRect.height, 10);
		}
		
		tooltip.style.left = finalX + 'px';
		tooltip.style.top = finalY + 'px';
		tooltipRafId = null;
	});
}
